const API_BASE_PATH = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/$/, '');
const DEFAULT_VAPID_PUBLIC_KEY =
  'BCyYEETfoveVjo_Rp39ewZvi-bvVW-bGJxrMCvkq_jyvRNtnR2Gw2E8LgrKaOfc1rkS6MLR_O1J9HJiUsdZh42g';
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || DEFAULT_VAPID_PUBLIC_KEY;

let registrationPromise = null;

const isWindowDefined = () => typeof window !== 'undefined';

export const isServiceWorkerSupported = () => isWindowDefined() && 'serviceWorker' in navigator;

export const isNotificationCapable = () =>
  isServiceWorkerSupported() && 'PushManager' in window && 'Notification' in window;

export function getNotificationPermission() {
  if (typeof Notification === 'undefined') {
    return 'default';
  }
  return Notification.permission;
}

export function registerBaseServiceWorker() {
  if (!isServiceWorkerSupported()) {
    return Promise.resolve(null);
  }
  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker.register('/service-worker.js');
  }
  return registrationPromise;
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function requestNotificationPermission() {
  const current = getNotificationPermission();
  if (current === 'granted') {
    return current;
  }
  if (current === 'denied') {
    return current;
  }
  return Notification.requestPermission();
}

async function getOrCreateSubscription(registration) {
  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    return existing;
  }
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
  });
}

async function saveSubscription(subscription, token) {
  const response = await fetch(`${API_BASE_PATH}/notifications/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ subscription }),
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || 'Failed to save push subscription');
  }
}

export async function enablePushNotifications(token) {
  if (!isNotificationCapable()) {
    throw new Error('Push notifications are not supported in this browser.');
  }
  const registration = await registerBaseServiceWorker();
  if (!registration) {
    throw new Error('Unable to register service worker.');
  }
  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted.');
  }
  const subscription = await getOrCreateSubscription(registration);
  await saveSubscription(subscription, token);
  return subscription;
}

export async function hasExistingPushSubscription() {
  if (!isNotificationCapable()) {
    return null;
  }
  const registration = await registerBaseServiceWorker();
  if (!registration) {
    return null;
  }
  return registration.pushManager.getSubscription();
}

export async function syncExistingSubscription(token) {
  if (!token || !isNotificationCapable()) {
    return false;
  }
  const registration = await registerBaseServiceWorker();
  if (!registration) {
    return false;
  }
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    return false;
  }
  await saveSubscription(subscription, token);
  return true;
}

export async function sendTestNotification(token, payload = {}) {
  const response = await fetch(`${API_BASE_PATH}/notifications/test`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      title: payload.title || 'Hours Tracker',
      body: payload.body || 'Background notification triggered successfully.',
      url: payload.url || '/',
    }),
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || 'Unable to trigger test notification');
  }
}
